import { useState } from "react";
import { giftPurchase } from "../../services/passService";

const COPY = {
  russian: {
    title: "Подарить абонемент",
    selectPass: "Выберите пакет",
    sendGiftTo: "Email для отправки подарочной карты",
    emailPlaceholder: "email@example.com",
    pay: "Перейти к оплате",
    invalidEmail: "Введите корректный email",
    error: "Не удалось создать платёж. Попробуйте снова.",
  },
  english: {
    title: "Give a pass as gift",
    selectPass: "Select a pass",
    sendGiftTo: "Email to send the gift card to",
    emailPlaceholder: "email@example.com",
    pay: "Proceed to payment",
    invalidEmail: "Please enter a valid email",
    error: "Failed to create payment. Please try again.",
  },
};

function getPassDescription(pass, language) {
  return (language === "russian" ? pass.descriptionRu : pass.descriptionEn) || "";
}

export default function GiftPurchaseModal({
  show,
  onClose,
  passTypes,
  selectedPass,
  setSelectedPass,
  getActiveDiscount,
  translatePassName,
  language,
  onError,
}) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const t = COPY[language] || COPY.russian;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPass) return;
    const trimmed = email.trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) {
      onError?.(t.invalidEmail);
      return;
    }
    setSubmitting(true);
    try {
      const res = await giftPurchase(selectedPass._id, trimmed);
      if (res?.paymentUrl) {
        window.location.href = res.paymentUrl;
        return;
      }
      onError?.(t.error);
    } catch (err) {
      onError?.(err?.message || t.error);
    } finally {
      setSubmitting(false);
    }
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex h-screen items-center justify-center z-50"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0 }}
    >
      <div
        className="bg-white rounded-t-3xl md:rounded-3xl w-full md:w-96 max-h-[90vh] overflow-y-auto p-4 md:p-6 relative"
        style={{ maxHeight: "90vh" }}
      >
        <button
          type="button"
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 text-xl font-bold"
          onClick={onClose}
          aria-label="Close"
        >
          ✕
        </button>

        <h2 className="text-base font-bold mb-4">{t.title}</h2>

        <form onSubmit={handleSubmit}>
          <p className="text-sm font-medium text-gray-700 mb-2">{t.selectPass}</p>
          <div className="space-y-2 mb-4">
            {passTypes.map((pass) => {
              const activeDiscount = getActiveDiscount(pass);
              const discountedPrice = activeDiscount
                ? Math.round(pass.basePrice * (1 - activeDiscount.discountPercent / 100))
                : pass.basePrice;
              return (
                <button
                  key={pass._id}
                  type="button"
                  onClick={() => setSelectedPass(pass)}
                  className={`w-full p-3 rounded-xl text-left border-2 transition ${
                    selectedPass?._id === pass._id
                      ? "border-brown bg-brown/10"
                      : "border-gray-300 hover:border-brown"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-semibold text-xs">{translatePassName(pass)}</p>
                      <p className="text-xs text-gray-600">
                        {getPassDescription(pass, language)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {activeDiscount && (
                        <span className="text-xs text-gray-400 line-through">
                          {pass.basePrice} ₪
                        </span>
                      )}
                      <span className="font-bold text-xs text-brown">{discountedPrice} ₪</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t.sendGiftTo}
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t.emailPlaceholder}
            className="w-full p-3 rounded-xl border-2 border-gray-300 focus:border-brown focus:ring-0 text-sm mb-4"
            required
            autoComplete="email"
          />

          <button
            type="submit"
            disabled={!selectedPass || submitting}
            className="w-full bg-brown text-white rounded-xl py-2 font-semibold text-sm hover:bg-beige hover:text-brown transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? "…" : t.pay}
          </button>
        </form>
      </div>
    </div>
  );
}
